// src/component/dashboard/AddProjectModal.jsx — subdomain-aware URL handling
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Loader2, Upload, X, FileText } from 'lucide-react';
import {
  setShowAddProjectModal,
  updateFormField,
  setFormErrors,
  clearFormError,
  addSelectedCountry,
  removeSelectedCountry,
  addSelectedVertical,
  removeSelectedVertical,
  resetForm
} from '../../store/slices/modalsSlice';
import { createBrand, fetchBrands, clearCreateStatus } from '../../store/slices/brandsSlice';
import CountryDropdown from './CountryDropdown';
import VerticalSelector from './VerticalSelector';
import {
  ADMIN_EMAIL,
  canOverrideVerification,
  verifyBrandUrl,
  verifyTradingBrandUrl,
  fetchTradingAccess,
  overrideBrandUrl
} from '../../services/urlVerification';

// Special account that can upload CSV to skip SEMrush — the same account that
// can override verification, so the address is kept in one place.
const CSV_UPLOAD_ALLOWED_EMAIL = ADMIN_EMAIL;

const AddProjectModal = () => {
  const dispatch = useDispatch();
  const { showAddProjectModal, addProjectForm } = useSelector(state => state.modals);
  const { createLoading, createError, createSuccess } = useSelector(state => state.brands);
  const { user } = useSelector(state => state.auth);

  // CSV upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const fileInputRef = useRef(null);

  // Check if current user can upload CSV
  const canUploadCsv = user?.email === CSV_UPLOAD_ALLOWED_EMAIL;

  // URL verification state
  const [urlVerified, setUrlVerified] = useState(false);
  const [verifyingUrl, setVerifyingUrl] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [overriding, setOverriding] = useState(false);

  // Admin only: state that a URL is a brand when the classifier disagrees.
  const canOverrideUrl = canOverrideVerification(user);

  // Which product is this brand being added for. Accounts without the Trading &
  // Investment vertical never see the choice and stay on 'igaming' — the form
  // then behaves exactly as it did before this existed.
  const [verticalMode, setVerticalMode] = useState('igaming');
  const [tradingAccess, setTradingAccess] = useState({ allowed: false, subVerticals: [] });
  const isTrading = verticalMode === 'trading';

  // WHERE the affiliates this brand finds get classified. 'cloud' is the
  // pipeline as it has always run — Zyte fetches the pages, Claude judges them.
  // 'local' hands that stage to a desktop running mars-local-classify, which
  // renders the pages on its own connection and judges them with the Claude
  // CLI, buying a page from Zyte only when a site blocks it. Same verdicts,
  // same cache table; it trades hours of wall-clock for the credits.
  const [classifyMode, setClassifyMode] = useState('cloud');

  useEffect(() => {
    if (!showAddProjectModal) return;
    let cancelled = false;
    fetchTradingAccess().then((access) => {
      if (!cancelled) setTradingAccess(access);
    });
    return () => { cancelled = true; };
  }, [showAddProjectModal]);

  // Switching product invalidates everything downstream of it: the picker now
  // offers a different list, and the URL was verified against the wrong
  // question ("is this a casino?" vs "is this a broker?").
  const handleVerticalModeChange = (mode) => {
    if (mode === verticalMode) return;
    setVerticalMode(mode);
    selectedVerticals.forEach(v => dispatch(removeSelectedVertical(v)));
    dispatch(clearFormError('verticals'));
    setUrlVerified(false);
    setVerificationMessage('');
  };

  const {
    brandName,
    brandUrl,
    selectedCountries,
    selectedVerticals,
    errors,
    successMessage
  } = addProjectForm;

  // Helper function to clean URL to domain (preserves subdomains, only strips www.)
  const convertToRootDomain = (url) => {
    if (!url) return '';

    let domain = url.trim();

    // If it starts with http:// or https://, extract the hostname
    if (domain.match(/^https?:\/\//i)) {
      try {
        const urlObj = new URL(domain);
        domain = urlObj.hostname;
      } catch {
        domain = domain.replace(/^https?:\/\//i, '');
      }
    }

    // Remove any trailing slashes and paths
    domain = domain.split('/')[0];

    // Only strip www. prefix — preserve other subdomains (m., play., sports., etc.)
    // as they can be separate properties with different data
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }

    return domain.toLowerCase();
  };

  // Helper function to normalize URL format (kept for validation)
  const normalizeUrl = (url) => {
    if (!url) return '';
    
    let normalizedUrl = url.trim();
    
    // If it doesn't start with http:// or https://, add https://
    if (!normalizedUrl.match(/^https?:\/\//i)) {
      normalizedUrl = 'https://' + normalizedUrl;
    }
    
    return normalizedUrl;
  };

  // Known TLDs and country-code TLDs — used to spot a country code typed in
  // front of a TLD (swiftcasino.es.com), not to limit which TLDs are allowed.
  const VALID_TLDS = new Set([
    // Common gTLDs
    'com','net','org','io','co','info','biz','me','tv','cc','app','dev','xyz','site',
    'online','live','pro','club','tech','store','shop','blog','ai','gg','top','vip',
    'ltd','group','world','space','digital','media','mobi','one','cloud','link','page',
    'name','asia','africa','eu',
    // Gambling-relevant gTLDs
    'casino','bet','poker','games','win','fun','betting','bingo','game','play','lotto','money',
    // Europe
    'uk','de','fr','es','it','nl','be','at','ch','se','no','dk','fi','pl','cz','pt','gr',
    'ie','hu','ro','bg','hr','rs','ua','tr','ru','lv','ee','lt','lu','sk','si','is',
    'al','ba','mk','md','ge','cy',
    // Gambling jurisdiction ccTLDs
    'ag','cw','gi','im','mt','vc','sc','mu','pa','cr',
    // Americas
    'us','ca','mx','br','ar','cl','pe','uy','ec','py','bo','tt','do',
    // Africa & Middle East
    'za','ng','ke','eg','sa','ae','il','gh','tz','ma','zw','ug','zm','mz','bw','cm','ci','sn',
    // Asia-Pacific
    'jp','cn','kr','in','au','nz','th','vn','my','sg','ph','id','tw','hk',
    'pk','bd','lk','np','mm','kh',
    // Compound TLDs
    'com.au','co.uk','co.nz','co.za','co.in','co.jp','co.kr','com.br','com.mx','com.ar',
    'com.co','com.pe','com.tr','com.ua','com.eg','com.sa','com.ng','org.uk','net.au',
    'co.il','co.id','co.th','com.sg','com.my','com.ph','com.vn','com.hk','com.tw'
  ]);

  // Helper function to validate domain format (supports paths like midarion.com/en/)
  const isValidDomain = (input) => {
    if (!input) return false;

    const trimmedInput = input.trim();

    let hostname;

    // Parse the hostname
    try {
      const urlStr = trimmedInput.match(/^https?:\/\//i) ? trimmedInput : 'https://' + trimmedInput;
      const url = new URL(urlStr);
      hostname = url.hostname;
    } catch {
      return false;
    }

    if (!hostname || !hostname.includes('.')) return false;

    // Extract TLD — check 2-part TLDs first (co.uk, com.au), then single TLDs
    const parts = hostname.split('.');
    if (parts.length < 2) return false;

    const lastTwo = parts.slice(-2).join('.');
    const lastOne = parts[parts.length - 1];

    // Known compound TLDs (co.uk, com.au, etc.)
    // Includes CentralNic shared public suffixes (eu.com, uk.com, …) — these look
    // like a country + TLD but are actually a single registrable suffix.
    const COMPOUND_TLDS = new Set([
      'co.uk','co.nz','co.za','co.in','co.jp','co.kr','co.il','co.id','co.th',
      'com.au','com.br','com.mx','com.ar','com.co','com.pe','com.tr','com.ua',
      'com.eg','com.sa','com.ng','com.sg','com.my','com.ph','com.vn','com.hk',
      'com.tw','org.uk','net.au','ac.uk','gov.uk','org.au','net.nz',
      // African betting-market compound ccTLDs (Zimbabwe co.zw, Kenya, Tanzania, …)
      'co.zw','co.ke','co.tz','co.ug','co.zm','co.mz','co.bw','com.gh','com.ng',
      'org.zw','net.zw',
      // CentralNic-style shared SLDs (publicly registrable as one unit)
      'eu.com','gb.com','gb.net','uk.com','uk.net','us.com','de.com','jp.net',
      'br.com','cn.com','ru.com','sa.com','se.com','no.com','hu.com','qc.com',
      'za.com','uy.com','ae.org','eu.org'
    ]);

    // For 3+ part hostnames (e.g. swiftcasino.es.com, example.co.uk)
    if (parts.length >= 3) {
      // If last two parts form a known compound TLD → valid (e.g. example.co.uk)
      if (COMPOUND_TLDS.has(lastTwo)) {
        return true;
      }
      // If last two parts are NOT a compound TLD but second-to-last is a country code
      // then it's likely a mistake like "swiftcasino.es.com" → invalid
      const secondToLast = parts[parts.length - 2];
      if (secondToLast.length === 2 && VALID_TLDS.has(secondToLast) && VALID_TLDS.has(lastOne)) {
        return false; // e.g. swiftcasino.es.com, mybrand.de.net
      }
    }

    // Any well-formed TLD. Checking against VALID_TLDS rejected real brands on
    // newer ones (yeno.market); Verify fetches the site, so a typo fails there.
    return /^([a-z]{2,63}|xn--[a-z0-9-]{1,59})$/i.test(lastOne);
  };

  // Handle URL change - reset verification
  const handleUrlChange = (value) => {
    // Block HTML/script injection
    if (/<[^>]*>/g.test(value)) return;
    // Block spaces and characters never valid in URLs
    if (value && /[\s<>"'{}|\\^`\[\]]/.test(value)) return;
    dispatch(updateFormField({ field: 'brandUrl', value }));
    if (errors.brandUrl) {
      dispatch(clearFormError('brandUrl'));
    }
    // Reset verification when URL changes
    setUrlVerified(false);
    setVerificationMessage('');
  };

  // Handle URL blur - clean up formatting but preserve path for verification
  const handleUrlBlur = () => {
    const trimmedUrl = brandUrl.trim();
    if (trimmedUrl) {
      // Only strip protocol (http:// https://) and www. prefix, but keep the path intact
      // so that URLs like midarion.com/en/ are verified with the correct path
      let cleaned = trimmedUrl
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '');
      if (cleaned && cleaned !== trimmedUrl) {
        dispatch(updateFormField({ field: 'brandUrl', value: cleaned }));
        // Reset verification since URL changed
        setUrlVerified(false);
        setVerificationMessage('');
      }
    }
  };

  // CSV file handling functions
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    // Detect separator
    const firstLine = lines[0];
    let separator = ',';
    if (firstLine.includes('\t')) separator = '\t';
    else if (firstLine.includes(';')) separator = ';';

    // Parse header
    const headers = firstLine.split(separator).map(h => h.trim().toLowerCase());

    // Find domain column
    const domainIndex = headers.findIndex(h =>
      h === 'domain' || h === 'backlink_url' || h === 'url' || h === 'referring domain'
    );

    if (domainIndex === -1) {
      console.error('No domain column found in CSV');
      return [];
    }

    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(separator);
      if (values[domainIndex] && values[domainIndex].trim()) {
        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx]?.trim() || '';
        });
        data.push(row);
      }
    }
    return data;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSV(text);
      if (parsed.length === 0) {
        alert('Could not parse CSV file. Please ensure it has a header row with a "Domain" column.');
        return;
      }
      setCsvFile(file);
      setCsvData(parsed);
    };
    reader.readAsText(file);
  };

  const handleRemoveCsv = () => {
    setCsvFile(null);
    setCsvData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Map API vertical names to VerticalSelector display names.
  const applyDetectedVerticals = (verticals = []) => {
    const verticalMap = {
      'Casino': 'Casino',
      'Sport': 'Sports Betting',
      'Sports': 'Sports Betting',
      'Poker': 'Poker',
      'Lottery': 'Lottery',
      'Bingo': 'Bingo',
      'Sweepstake': 'Sweepstake',
      'Crypto': 'Crypto'
    };

    verticals.forEach(vertical => {
      const mappedVertical = verticalMap[vertical] || vertical;
      if (!selectedVerticals.includes(mappedVertical)) {
        dispatch(addSelectedVertical(mappedVertical));
      }
    });
  };

  // Verify URL. A domain that has been verified before answers from
  // verified_brand_urls without the fetch or the AI call.
  const handleVerifyUrl = async () => {
    const trimmedUrl = brandUrl.trim();
    if (!trimmedUrl) {
      setVerificationMessage('Please enter a URL first');
      return;
    }

    if (!isValidDomain(trimmedUrl)) {
      setVerificationMessage('Please enter a valid domain format first');
      return;
    }

    setVerifyingUrl(true);
    setVerificationMessage('');

    // Same call, different question. The trading check asks "is this a
    // forex/CFD broker?" and returns the instruments it offers, which
    // applyDetectedVerticals then ticks in the sub-category picker.
    const result = isTrading
      ? await verifyTradingBrandUrl(normalizeUrl(trimmedUrl))
      : await verifyBrandUrl(normalizeUrl(trimmedUrl));

    setUrlVerified(result.verified);
    setVerificationMessage(result.message);
    if (result.verified) applyDetectedVerticals(result.verticals);
    setVerifyingUrl(false);
  };

  /**
   * Admin only: accept the URL as a brand and remember it.
   *
   * The verticals already ticked on the form are what gets stored, because at
   * this point the admin knows what the site is and the classifier did not.
   * The domain is verified everywhere afterwards, not just in this form.
   */
  const handleOverrideUrl = async () => {
    const trimmedUrl = brandUrl.trim();
    if (!trimmedUrl) {
      setVerificationMessage('Please enter a URL first');
      return;
    }

    setOverriding(true);
    const result = await overrideBrandUrl(normalizeUrl(trimmedUrl), selectedVerticals);

    setUrlVerified(result.verified);
    setVerificationMessage(result.message);
    setOverriding(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    const trimmedBrandName = brandName.trim();
    const trimmedBrandUrl = brandUrl.trim();
    
    if (!trimmedBrandName) {
      newErrors.brandName = 'Brand name is required';
    } else if (/^https?:\/\//i.test(trimmedBrandName) || /^www\./i.test(trimmedBrandName) || /\.[a-z]{2,}(\/|$)/i.test(trimmedBrandName)) {
      newErrors.brandName = 'Please enter a brand name, not a URL. Use the Brand URL field below for the website address.';
    }
    
    if (!trimmedBrandUrl) {
      newErrors.brandUrl = 'Brand URL/Domain is required';
    } else if (!isValidDomain(trimmedBrandUrl)) {
      newErrors.brandUrl = 'Please enter a valid domain (e.g., example.com, https://www.example.com, subdomain.example.co.uk)';
    }
    
    if (selectedCountries.length === 0) {
      newErrors.countries = 'Please select at least one country';
    }
    
    if (selectedVerticals.length === 0) {
      newErrors.verticals = isTrading
        ? 'Please select at least one sub category'
        : 'Please select at least one vertical';
    }
    
    dispatch(setFormErrors(newErrors));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Convert URL to root domain before sending to backend
    const rootDomain = convertToRootDomain(brandUrl.trim());

    // Put primary country first so backend uses it for Zyte SERP geocode
    const { primaryCountry } = addProjectForm;
    const orderedCountries = primaryCountry
      ? [primaryCountry, ...selectedCountries.filter(c => c !== primaryCountry)]
      : selectedCountries;

    const brandData = {
      brandName: brandName.trim(),
      brandUrl: rootDomain, // Send root domain instead of full URL
      // In trading mode these are the INSTRUMENTS; the backend fixes the
      // vertical to 'Trading & Investment' and stores these as sub_vertical.
      selectedVerticals: selectedVerticals,
      selectedCountries: orderedCountries,
      // Stripped from the body by the thunk — it only chooses the endpoint.
      verticalMode
    };

    // Only meaningful for the trading endpoint, and only sent from there: the
    // iGaming pipeline has no local classifier to hand the stage to.
    if (isTrading) brandData.classifyMode = classifyMode;

    // If CSV data is provided, add it to the request (for special account)
    if (canUploadCsv && csvData.length > 0) {
      brandData.csvBacklinks = csvData;
      brandData.skipSemrush = true;
    }

    try {
      // V2: the API responds in seconds with the created brand — the card
      // appears immediately and shows real pipeline progress on the dashboard.
      await dispatch(createBrand(brandData)).unwrap();

      // Close the modal and reset the form; the brand card takes over from here
      dispatch(setShowAddProjectModal(false));
      dispatch(resetForm());
      dispatch(clearCreateStatus());
      setCsvFile(null);
      setCsvData([]);
      setUrlVerified(false);
      setVerificationMessage('');
      setVerticalMode('igaming');

      // Refresh the list so metrics/favicon come from the server
      dispatch(fetchBrands({ limit: 5, offset: 0 }));
    } catch (error) {
      // Modal stays open; createError from the slice is rendered above the form
    }
  };

  const handleClose = () => {
    dispatch(setShowAddProjectModal(false));
    dispatch(resetForm());
    dispatch(clearCreateStatus());
    // Reset verification state
    setUrlVerified(false);
    setVerificationMessage('');
    // Reset CSV state
    setCsvFile(null);
    setCsvData([]);
  };

  const isFormValid = () => {
    return (
      brandName.trim() !== '' &&
      brandUrl.trim() !== '' &&
      isValidDomain(brandUrl.trim()) &&
      selectedCountries.length > 0 &&
      selectedVerticals.length > 0 &&
      urlVerified
    );
  };

  return (
    <>
      {/* Main Add Project Modal */}
      {showAddProjectModal && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-gray-200 dark:border-slate-700 animate-slideUp">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-6 py-5 border-b border-blue-700 dark:border-blue-900">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add New Project
                  </h2>
                  <p className="text-blue-100 text-sm mt-1">Track your brand's affiliate network</p>
                </div>
                <button
                  onClick={handleClose}
                  className="text-blue-100 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all"
                  aria-label="Close modal"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">

              <div className="space-y-5">
                {(successMessage || createSuccess) && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 rounded-xl text-sm flex items-start gap-3 animate-slideDown">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{successMessage || 'Brand created successfully!'}</span>
                  </div>
                )}

                {(errors.submit || createError) && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-xl text-sm flex items-start gap-3 animate-slideDown">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span>{errors.submit || createError}</span>
                  </div>
                )}

                {/* Only accounts granted the Trading & Investment vertical get
                    this choice. Everyone else sees the form unchanged. */}
                {tradingAccess.allowed && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                      What are you adding this brand for?
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'igaming', title: 'iGaming', hint: 'Casino, betting, poker…' },
                        { id: 'trading', title: 'Trading & Investment', hint: 'Forex / CFD brokers' }
                      ].map(option => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => handleVerticalModeChange(option.id)}
                          className={`text-left px-4 py-3 rounded-xl border transition-all ${
                            verticalMode === option.id
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-500 ring-2 ring-blue-500/30'
                              : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                          }`}
                        >
                          <span className="block text-sm font-semibold text-gray-800 dark:text-slate-100">{option.title}</span>
                          <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">{option.hint}</span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                      This decides how the affiliates found for this brand are classified.
                    </p>
                  </div>
                )}

                {/* Where that classification runs. Trading only for now: it is
                    the vertical whose classify stage the local worker knows how
                    to do, and the two paths agree on the verdict — they differ
                    in what they cost and how long they take. */}
                {tradingAccess.allowed && isTrading && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                      Where should classification run?
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'cloud', title: 'Cloud', hint: 'Fast — spends Zyte + Claude credits' },
                        { id: 'local', title: 'Local server', hint: 'Free — a desktop does the work' }
                      ].map(option => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setClassifyMode(option.id)}
                          className={`text-left px-4 py-3 rounded-xl border transition-all ${
                            classifyMode === option.id
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-500 ring-2 ring-blue-500/30'
                              : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                          }`}
                        >
                          <span className="block text-sm font-semibold text-gray-800 dark:text-slate-100">{option.title}</span>
                          <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">{option.hint}</span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                      {classifyMode === 'local'
                        ? 'The brand is created now and its domains are queued for a desktop running the local classifier. Nothing is classified until one of those workers is running, and a large list can take hours.'
                        : 'Classified on the server as soon as the domains are collected.'}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Brand Name *
                  </label>
                  <input
                    type="text"
                    value={brandName}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Only allow letters, numbers, spaces, hyphens, dots, ampersands
                      if (val && !/^[a-zA-Z0-9\s.\-&]*$/.test(val)) {
                        return;
                      }
                      // Block HTML/script injection
                      if (/<[^>]*>/g.test(val)) {
                        return;
                      }
                      dispatch(updateFormField({ field: 'brandName', value: val }));
                      if (errors.brandName) {
                        dispatch(clearFormError('brandName'));
                      }
                      // Warn if user enters a URL in the brand name field
                      if (/^https?:\/\//i.test(val.trim()) || /^www\./i.test(val.trim()) || /\.[a-z]{2,}(\/|$)/i.test(val.trim())) {
                        dispatch(setFormErrors({ ...errors, brandName: 'Please enter a brand name, not a URL. Use the Brand URL field below for the website address.' }));
                      }
                    }}
                    placeholder="e.g., BetMaze, CasinoKings"
                    className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-all dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 ${
                      errors.brandName
                        ? 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-300 bg-red-50 dark:bg-red-900/20'
                        : 'border-gray-200 dark:border-slate-600 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300 dark:hover:border-slate-500'
                    }`}
                  />
                  {errors.brandName && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.brandName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    Brand URL/Domain *
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={brandUrl}
                        onChange={(e) => handleUrlChange(e.target.value)}
                        onBlur={handleUrlBlur}
                        placeholder="betmaze.com or https://www.example.com"
                        className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-all dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 ${
                          errors.brandUrl
                            ? 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-300 bg-red-50 dark:bg-red-900/20 pr-10'
                            : urlVerified
                              ? 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/30 focus:ring-green-500 pr-10'
                              : 'border-gray-200 dark:border-slate-600 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300 dark:hover:border-slate-500'
                        }`}
                      />
                      {urlVerified && (
                        <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleVerifyUrl}
                      disabled={verifyingUrl || !brandUrl.trim() || !isValidDomain(brandUrl.trim())}
                      className={`px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all shadow-sm ${
                        urlVerified
                          ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-green-200 dark:shadow-green-900/50'
                          : verifyingUrl
                            ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-slate-400 cursor-wait'
                            : !brandUrl.trim() || !isValidDomain(brandUrl.trim())
                              ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                              : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-blue-200 dark:shadow-blue-900/50'
                      }`}
                    >
                      {verifyingUrl ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Verifying
                        </span>
                      ) : urlVerified ? (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Verified
                        </span>
                      ) : (
                        'Verify'
                      )}
                    </button>
                  </div>
                  {errors.brandUrl && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.brandUrl}
                    </p>
                  )}
                  {verificationMessage && (
                    <div className={`mt-3 text-sm p-3 rounded-xl flex items-start gap-2 ${
                      urlVerified
                        ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                    }`}>
                      {urlVerified ? (
                        <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span>{verificationMessage}</span>
                    </div>
                  )}
                  {/* Admin only. The classifier rejects sites it should not —
                      a thin landing page, a geo-blocked fetch, a brand that
                      renders in JavaScript. Rather than argue with it, say so
                      once: the domain is stored as verified and every form
                      accepts it from then on. */}
                  {canOverrideUrl && !urlVerified && brandUrl.trim() && (
                    <button
                      type="button"
                      onClick={handleOverrideUrl}
                      disabled={overriding}
                      className="mt-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:cursor-wait disabled:opacity-60"
                    >
                      {overriding
                        ? 'Saving override…'
                        : 'Override — mark this URL as verified'}
                    </button>
                  )}
                  {!urlVerified && brandUrl.trim() && isValidDomain(brandUrl.trim()) && !verificationMessage && (
                    <div className="mt-3 text-sm p-3 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>
                        {isTrading
                          ? "Please verify the URL to confirm it's a forex/CFD broker or trading platform"
                          : "Please verify the URL to confirm it's a gambling/betting website"}
                      </span>
                    </div>
                  )}
                </div>

                <CountryDropdown />
                {isTrading ? (
                  <VerticalSelector
                    options={tradingAccess.subVerticals}
                    label="Sub Category *"
                    placeholder="Select sub category..."
                  />
                ) : (
                  <VerticalSelector />
                )}

                {/* CSV Upload Section - Only visible for special account */}
                {canUploadCsv && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                      Backlinks CSV (Optional)
                    </label>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      Upload a CSV file with backlinks to skip SEMrush API call and save costs.
                    </p>

                    {!csvFile ? (
                      <div
                        className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-4 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept=".csv"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <Upload className="w-8 h-8 mx-auto text-gray-400 dark:text-slate-500 mb-2" />
                        <p className="text-sm text-gray-600 dark:text-slate-400">
                          Click to upload CSV
                        </p>
                        <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                          CSV must have a "Domain" column
                        </p>
                      </div>
                    ) : (
                      <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
                            <div>
                              <p className="text-sm font-medium text-green-700 dark:text-green-400">
                                {csvFile.name}
                              </p>
                              <p className="text-xs text-green-600 dark:text-green-500">
                                {csvData.length} backlinks loaded
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveCsv}
                            className="p-1 text-green-600 dark:text-green-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <p className="text-xs text-green-600 dark:text-green-500 mt-2">
                          SEMrush API call will be skipped
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-slate-700 mt-6">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-5 py-3 border-2 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-gray-300 dark:hover:border-slate-500 transition-all font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!isFormValid() || createLoading}
                    className={`flex-1 px-5 py-3 rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-sm ${
                      (!isFormValid() || createLoading)
                        ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-slate-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-blue-200 dark:shadow-blue-900/50 hover:shadow-md'
                    }`}
                  >
                    {createLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                    {createLoading ? (
                      'Creating Project...'
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Project
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default AddProjectModal;